import type { WatermarkSettingsFormData } from './types';

import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import type { UUID } from '~/types/common';

import { type SuccessResponse } from '@vestigas/data';

import { queryKeysCompany } from '../queryKeys';

import { updateCompanyWatermarkSettings } from './updateCompanyWatermarkSettings';

/**
 * Custom hook for updating a company's watermark settings using mutations.
 *
 * @param options - Optional mutation options
 * @returns TanStack Query mutation object
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/update_watermark_settings_vadmin_company__company_id__watermark_settings_put
 */
export const useMutationUpdateCompanyWatermarkSettings = (
  options?: Omit<
    UseMutationOptions<
      SuccessResponse,
      Error,
      {
        companyId: UUID;
        watermarkSettings: WatermarkSettingsFormData;
      }
    >,
    'mutationFn' | 'onSettled'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    SuccessResponse,
    Error,
    {
      companyId: UUID;
      watermarkSettings: WatermarkSettingsFormData;
    }
  >({
    async mutationFn({ companyId, watermarkSettings }) {
      return updateCompanyWatermarkSettings(companyId, watermarkSettings);
    },
    onSettled(_data, _error, { companyId }) {
      // Invalidate the watermark settings query to refetch fresh data
      void queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getWatermarkSettings(companyId),
      });
    },
    ...options,
  });
};
